/**
 * Iter 189 — One-Tap Trade Firing (React dashboard mirror).
 *
 * This component is the web-side twin of the TM `oneTapTab.js` sidebar
 * module. It fetches the latest signal from a user-chosen source
 * (GodMode / SNAP-5 / Chart Scanner / Max-Accuracy ensemble / auto-best),
 * shows one big CALL and one big PUT button pre-tinted to the
 * engine's current direction, and logs the fire into `fire_history`
 * (the actual DOM click still happens inside the Pocket Option tab via
 * Tampermonkey — this UI mirror is for sanity-check + preset editing).
 *
 * Safety flow:
 *   • Default: 2-second cancel countdown overlay (configurable).
 *   • Toggle `instant_fire` to skip the countdown.
 *   • Any tap below the configured min-confidence shows a warning badge.
 *
 * Preset editor at the bottom lets the user save per-asset amount + expiry
 * so the TM sidebar fires with the right stake without having to hunt
 * through the PO UI.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Zap, TrendingUp, TrendingDown, RefreshCw, Trash2, Plus, X, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_ASSETS = [
  "EURUSD_OTC", "GBPUSD_OTC", "USDJPY_OTC", "AUDCAD_OTC",
  "AUDUSD_OTC", "EURJPY_OTC", "USDCHF_OTC", "USDCAD_OTC",
];
const SOURCES = [
  { value: "max_accuracy", label: "Max-Accuracy Ensemble" },
  { value: "godmode",      label: "GodMode (6-layer)" },
  { value: "snap5",        label: "SNAP-5 (5-sec)" },
  { value: "scanner",      label: "Chart Scanner" },
  { value: "auto",         label: "Auto — Highest Confidence" },
];
const EXPIRIES = [5, 15, 30, 60, 120, 180, 300];

const USER_ID = "default";

/* ────────────────────────────── helpers ────────────────────────────── */
function dirStyle(d) {
  if (d === "CALL") return "bg-emerald-900/60 text-emerald-200 border-emerald-500/60";
  if (d === "PUT")  return "bg-rose-900/60 text-rose-200 border-rose-500/60";
  return "bg-slate-800 text-slate-300 border-slate-600";
}

/* ─────────────────────────── countdown overlay ─────────────────────── */
function CountdownOverlay({ seconds, direction, onCancel, onComplete }) {
  const [left, setLeft] = useState(seconds);
  const timerRef = useRef(null);
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setTimeout(onComplete, 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [onComplete]);

  const colors = direction === "CALL"
    ? "from-emerald-600 to-emerald-900"
    : "from-rose-600 to-rose-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
         data-testid="onetap-countdown-overlay">
      <div className={`relative bg-gradient-to-br ${colors} p-8 rounded-2xl shadow-2xl border-4 border-white/20 min-w-[320px]`}>
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-white/70 mb-2">
            Firing in
          </div>
          <div className="text-8xl font-black text-white tabular-nums"
               data-testid="onetap-countdown-value">
            {left}
          </div>
          <div className="text-lg font-bold text-white mt-2">
            {direction} TRADE
          </div>
          <Button
            data-testid="onetap-cancel-btn"
            variant="outline"
            className="mt-5 bg-white/10 hover:bg-white/20 border-white/30 text-white"
            onClick={onCancel}
          >
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── main view ───────────────────────────── */
export default function OneTapFire() {
  const [cfg, setCfg] = useState(null);
  const [presets, setPresets] = useState([]);
  const [asset, setAsset] = useState("EURUSD_OTC");
  const [signal, setSignal] = useState(null);
  const [sigErr, setSigErr] = useState(null);
  const [loadingSig, setLoadingSig] = useState(false);
  const [firing, setFiring] = useState(false);
  const [pendingDir, setPendingDir] = useState(null);

  const [newPreset, setNewPreset] = useState({ asset: "", amount: 1.0, expiry_s: 60 });

  /* ------------------------------ config ----------------------------- */
  const loadCfg = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/one-tap/config`, { params: { user_id: USER_ID } });
      if (r.data?.ok) setCfg(r.data.config);
    } catch (e) {
      toast.error("Failed to load One-Tap config");
    }
  }, []);
  const saveCfg = useCallback(async (patch) => {
    try {
      const r = await axios.post(`${API}/one-tap/config`, { user_id: USER_ID, ...patch });
      if (r.data?.ok) setCfg(r.data.config);
    } catch (e) {
      toast.error("Failed to save config");
    }
  }, []);

  /* ----------------------------- presets ----------------------------- */
  const loadPresets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/one-tap/presets`, { params: { user_id: USER_ID } });
      if (r.data?.ok) setPresets(r.data.presets || []);
    } catch (_e) { /* soft */ }
  }, []);
  const savePreset = useCallback(async (p) => {
    try {
      const r = await axios.post(`${API}/one-tap/preset`, {
        user_id: USER_ID,
        asset:   (p.asset || "").toUpperCase(),
        amount:  Number(p.amount),
        expiry_s: Number(p.expiry_s),
      });
      if (r.data?.ok) {
        toast.success(`Preset saved for ${r.data.preset.asset}`);
        await loadPresets();
      } else {
        toast.error(`Save failed: ${r.data?.error || r.data?.reason}`);
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  }, [loadPresets]);
  const deletePreset = useCallback(async (a) => {
    try {
      await axios.delete(`${API}/one-tap/preset`, { params: { user_id: USER_ID, asset: a } });
      await loadPresets();
      toast.success(`Preset removed for ${a}`);
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  }, [loadPresets]);

  /* ------------------------------ signal ----------------------------- */
  const activePreset = useMemo(
    () => presets.find((p) => p.asset === asset) || null,
    [presets, asset],
  );
  const activeAmount = activePreset?.amount ?? cfg?.default_amount ?? 1.0;
  const activeExpiry = activePreset?.expiry_s ?? cfg?.default_expiry_s ?? 60;

  const fetchSignal = useCallback(async () => {
    if (!cfg) return;
    setLoadingSig(true); setSigErr(null);
    try {
      const r = await axios.get(`${API}/one-tap/signal`, {
        params: {
          user_id: USER_ID,
          asset,
          source: cfg.source,
          timeframe: "5s",
          expiry_s: activeExpiry,
        },
      });
      if (r.data?.ok) {
        setSignal(r.data.signal);
        setSigErr(r.data.signal ? null : (r.data.reason || "no_signal"));
      } else {
        setSigErr(r.data?.error || "signal_failed");
      }
    } catch (e) {
      setSigErr(e.message);
    } finally {
      setLoadingSig(false);
    }
  }, [asset, cfg, activeExpiry]);

  useEffect(() => { loadCfg(); loadPresets(); }, [loadCfg, loadPresets]);
  useEffect(() => { if (cfg) fetchSignal(); /* eslint-disable-next-line */ }, [cfg?.source, asset]);

  /* ------------------------------- fire ------------------------------ */
  const confirmFire = useCallback(async (direction) => {
    setFiring(true);
    try {
      const r = await axios.post(`${API}/one-tap/fire`, {
        user_id: USER_ID,
        asset,
        direction,
        amount: Number(activeAmount),
        expiry_s: Number(activeExpiry),
        confidence: signal?.confidence ?? null,
        source: cfg?.source || "manual",
        client_ts: Date.now(),
      });
      if (r.data?.ok) {
        toast.success(`${direction} fired on ${asset} · trade ${r.data.trade_id}`, {
          description: `Amount $${activeAmount} · Expiry ${activeExpiry}s · Source ${cfg?.source}`,
        });
      } else {
        toast.error(`Fire failed: ${r.data?.error || r.data?.reason}`);
      }
    } catch (e) {
      toast.error(`Fire failed: ${e.message}`);
    } finally {
      setFiring(false);
      setPendingDir(null);
    }
  }, [asset, activeAmount, activeExpiry, cfg?.source, signal?.confidence]);

  const onTap = useCallback((direction) => {
    if (!cfg) return;
    if (cfg.instant_fire) { confirmFire(direction); return; }
    setPendingDir(direction);
  }, [cfg, confirmFire]);

  /* ─────────────────────────────── render ─────────────────────────── */
  if (!cfg) {
    return (
      <div className="p-6 text-slate-400" data-testid="onetap-loading">Loading One-Tap config…</div>
    );
  }

  const dir = signal?.direction;
  const conf = signal?.confidence ?? 0;
  const lowConf = cfg.min_confidence > 0 && conf < cfg.min_confidence;

  return (
    <div className="p-6 space-y-6" data-testid="onetap-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 flex items-center gap-3">
            <Zap className="h-7 w-7 text-amber-400" /> One-Tap Trade Firing
          </h1>
          <p className="text-slate-500 mt-1">
            Single click. Latest signal, right-sized stake, mirrored into the TM sidebar.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchSignal}
          disabled={loadingSig}
          data-testid="onetap-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loadingSig ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* ----- Fire panel ------------------------------------------------ */}
      <Card className="border-amber-500/30 bg-slate-900/60">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-slate-100">
            <span className="flex items-center gap-2">
              <span>Fire panel</span>
              <Badge variant="outline" className="border-amber-500/50 text-amber-300 text-xs">
                {cfg.source}
              </Badge>
            </span>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-400">Asset</Label>
              <Select value={asset} onValueChange={setAsset}>
                <SelectTrigger className="h-8 w-[180px] bg-slate-800 border-slate-700"
                               data-testid="onetap-asset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_ASSETS.map((a) => (
                    <SelectItem key={a} value={a} data-testid={`onetap-asset-${a}`}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* source selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Signal source</Label>
            <Select value={cfg.source} onValueChange={(v) => saveCfg({ source: v })}>
              <SelectTrigger className="h-8 w-[260px] bg-slate-800 border-slate-700"
                             data-testid="onetap-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s.value} value={s.value}
                              data-testid={`onetap-source-${s.value}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500 ml-2">
              Amount <b className="text-amber-300" data-testid="onetap-amount">${activeAmount}</b>
              {" · "}
              Expiry <b className="text-amber-300" data-testid="onetap-expiry">{activeExpiry}s</b>
              {activePreset
                ? <span className="ml-1 text-emerald-400">(preset)</span>
                : <span className="ml-1 text-slate-600">(default)</span>}
            </span>
          </div>

          {/* current signal pill */}
          <div className={`flex items-center gap-4 rounded-lg border p-4 ${dirStyle(dir)}`}
               data-testid="onetap-signal-row">
            {loadingSig ? (
              <span className="text-slate-400 text-sm">Fetching signal…</span>
            ) : sigErr ? (
              <span className="text-amber-300 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> {sigErr}
              </span>
            ) : signal ? (
              <>
                <div className="font-black text-2xl tracking-widest">{dir}</div>
                <div className="flex-1">
                  <div className="text-xs opacity-80 uppercase">Confidence</div>
                  <div className="text-lg font-bold tabular-nums"
                       data-testid="onetap-confidence">{(conf * 100).toFixed(1)}%</div>
                </div>
                <div className="text-xs opacity-70 text-right">
                  <div>{signal.reason}</div>
                  <div className="font-mono">src: {signal.source}</div>
                </div>
                {signal.fallback_from && (
                  <Badge variant="outline" className="border-sky-400 text-sky-300" data-testid="onetap-fallback-badge">
                    fallback: {signal.fallback_from} had no fire
                  </Badge>
                )}
                {lowConf && (
                  <Badge variant="outline" className="border-amber-400 text-amber-300" data-testid="onetap-lowconf-warn">
                    below min conf
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-slate-400 text-sm">No signal yet — tap Refresh.</span>
            )}
          </div>

          {/* fire buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              data-testid="onetap-call-btn"
              onClick={() => onTap("CALL")}
              disabled={firing || !!pendingDir}
              className={`
                relative overflow-hidden rounded-2xl py-8 border-2 transition-all
                font-black text-2xl tracking-wider uppercase
                bg-gradient-to-br from-emerald-500 to-emerald-800
                hover:from-emerald-400 hover:to-emerald-700 border-emerald-400/60
                text-white shadow-lg shadow-emerald-900/50
                disabled:opacity-50 disabled:cursor-not-allowed
                ${dir === "CALL" ? "ring-4 ring-emerald-300/70" : ""}
              `}
            >
              <TrendingUp className="h-7 w-7 inline mr-2" /> CALL
              <div className="text-xs font-normal normal-case opacity-80 mt-1">
                buy / up
              </div>
            </button>
            <button
              data-testid="onetap-put-btn"
              onClick={() => onTap("PUT")}
              disabled={firing || !!pendingDir}
              className={`
                relative overflow-hidden rounded-2xl py-8 border-2 transition-all
                font-black text-2xl tracking-wider uppercase
                bg-gradient-to-br from-rose-500 to-rose-800
                hover:from-rose-400 hover:to-rose-700 border-rose-400/60
                text-white shadow-lg shadow-rose-900/50
                disabled:opacity-50 disabled:cursor-not-allowed
                ${dir === "PUT" ? "ring-4 ring-rose-300/70" : ""}
              `}
            >
              <TrendingDown className="h-7 w-7 inline mr-2" /> PUT
              <div className="text-xs font-normal normal-case opacity-80 mt-1">
                sell / down
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* ----- Safety ---------------------------------------------------- */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-100 text-lg">Safety</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-200">Instant fire</Label>
              <p className="text-xs text-slate-500">Skip the countdown and fire immediately on tap.</p>
            </div>
            <Switch
              checked={!!cfg.instant_fire}
              onCheckedChange={(v) => saveCfg({ instant_fire: v })}
              data-testid="onetap-instant-switch"
            />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-slate-300 w-40">Cancel countdown</Label>
            <Select
              value={String(cfg.countdown_s)}
              onValueChange={(v) => saveCfg({ countdown_s: parseInt(v, 10) })}
            >
              <SelectTrigger className="h-8 w-[140px] bg-slate-800 border-slate-700"
                             data-testid="onetap-countdown-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0,1,2,3,5].map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}s</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">Ignored when Instant Fire is ON.</span>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-slate-300 w-40">Min confidence</Label>
            <Input
              type="number" min="0" max="1" step="0.05"
              className="w-[120px] bg-slate-800 border-slate-700"
              value={cfg.min_confidence}
              onChange={(e) => saveCfg({ min_confidence: parseFloat(e.target.value || "0") })}
              data-testid="onetap-minconf-input"
            />
            <span className="text-xs text-slate-500">Taps below this get a warning badge.</span>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-slate-300 w-40">Default amount</Label>
            <Input
              type="number" min="0.1" step="0.1"
              className="w-[120px] bg-slate-800 border-slate-700"
              value={cfg.default_amount}
              onChange={(e) => saveCfg({ default_amount: parseFloat(e.target.value || "1") })}
              data-testid="onetap-defaultamt-input"
            />
            <Label className="text-slate-300 ml-6">Default expiry</Label>
            <Select
              value={String(cfg.default_expiry_s)}
              onValueChange={(v) => saveCfg({ default_expiry_s: parseInt(v, 10) })}
            >
              <SelectTrigger className="h-8 w-[120px] bg-slate-800 border-slate-700"
                             data-testid="onetap-defaultexp-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRIES.map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}s</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ----- Presets --------------------------------------------------- */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-100 text-lg">Per-asset presets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {presets.length === 0 && (
              <div className="text-sm text-slate-500" data-testid="onetap-no-presets">
                No presets yet — add one below to lock in a bespoke amount + expiry per asset.
              </div>
            )}
            {presets.map((p) => (
              <div key={p.asset}
                   className="flex items-center gap-3 bg-slate-800/50 rounded-md px-3 py-2"
                   data-testid={`onetap-preset-${p.asset}`}>
                <div className="font-mono text-sm text-slate-200 w-32">{p.asset}</div>
                <div className="text-sm text-slate-300">${p.amount}</div>
                <div className="text-sm text-slate-500">· {p.expiry_s}s</div>
                <div className="flex-1" />
                <Button
                  size="sm" variant="ghost"
                  onClick={() => deletePreset(p.asset)}
                  data-testid={`onetap-del-preset-${p.asset}`}
                >
                  <Trash2 className="h-4 w-4 text-rose-400" />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-end gap-3 flex-wrap bg-slate-800/40 p-3 rounded-md">
            <div>
              <Label className="text-xs text-slate-400">Asset</Label>
              <Input
                className="w-[160px] bg-slate-800 border-slate-700"
                placeholder="EURUSD_OTC"
                value={newPreset.asset}
                onChange={(e) => setNewPreset({ ...newPreset, asset: e.target.value })}
                data-testid="onetap-new-asset"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Amount ($)</Label>
              <Input
                type="number" min="0.1" step="0.1"
                className="w-[110px] bg-slate-800 border-slate-700"
                value={newPreset.amount}
                onChange={(e) => setNewPreset({ ...newPreset, amount: parseFloat(e.target.value || "1") })}
                data-testid="onetap-new-amount"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Expiry</Label>
              <Select
                value={String(newPreset.expiry_s)}
                onValueChange={(v) => setNewPreset({ ...newPreset, expiry_s: parseInt(v, 10) })}
              >
                <SelectTrigger className="h-9 w-[110px] bg-slate-800 border-slate-700"
                               data-testid="onetap-new-expiry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRIES.map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}s</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => {
                if (!newPreset.asset) { toast.error("Asset required"); return; }
                savePreset(newPreset);
                setNewPreset({ asset: "", amount: 1.0, expiry_s: 60 });
              }}
              data-testid="onetap-save-preset-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Save preset
            </Button>
          </div>
        </CardContent>
      </Card>

      {pendingDir && (
        <CountdownOverlay
          seconds={Math.max(1, cfg.countdown_s || 2)}
          direction={pendingDir}
          onCancel={() => setPendingDir(null)}
          onComplete={() => confirmFire(pendingDir)}
        />
      )}
    </div>
  );
}
